import React from 'react';

import { useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { LINE_ITEM_CART_ITEM, LINE_ITEM_ITEM } from '../../util/types';

import { lineItemQuantity } from '../../util/custom/lineItemQuantity';

import css from './OrderBreakdown.module.css';

/**
 * CL-CART-01 Task 15 — the basket's other items, on the order breakdown.
 *
 * WHY THIS EXISTS
 *
 * A Sharetribe transaction points at exactly one listing. The basket's anchor carries
 * `line-item/item` and the template renders it, with its title, through
 * `LineItemBasePriceMaybe`. Every other item rides along as `line-item/cart-item`, which the
 * template has never heard of — so it fell to `LineItemUnknownItemsMaybe`, whose label comes
 * from `humanizeLineItemCode`. Every extra item on every basket order read **"Cart item x 1"**,
 * while its real title sat unread in `protectedData.cart.items`.
 *
 * WHY TITLES ARE MATCHED BY POSITION
 *
 * The line items carry no listing id — Sharetribe stores `code`, `unitPrice`, `quantity`,
 * `includeFor` and the totals it computes, and drops anything else. `protectedData.cart.items`
 * is written in the same request, from the same array, in the same order, so position is the
 * only join available and is correct by construction.
 *
 * It is still checked. Pairing a title with somebody else's price is worse than showing no
 * title at all, so a length mismatch falls back to a neutral label rather than guessing.
 *
 * MONEY COMES FROM THE LINE ITEMS, NOT THE SNAPSHOT
 *
 * The snapshot's `unitPriceInSubunits` is a record of what the price was; the line item is what
 * was actually charged. They agree today, and if they ever stop, the charge is the truth.
 *
 * @param {Object} props
 * @param {Array} props.lineItems - the transaction's line items
 * @param {Object} props.cart - `protectedData.cart`; absent on every pre-Milestone-B order
 */
const LineItemCartItemsMaybe = props => {
  const { lineItems, cart } = props;
  const intl = useIntl();

  const hasBasket = (lineItems || []).some(li => li.code === LINE_ITEM_CART_ITEM && !li.reversal);

  // Not a basket: the template's own rows already say everything, and this stands down.
  if (!hasBasket) return null;

  // The ANCHOR is rendered here too, not by LineItemBasePriceMaybe — OrderBreakdown suppresses
  // that for a basket. Otherwise the first row reads "Unit price" while every row under it
  // reads a title: one list, two different ways of naming a thing.
  //
  // Anchor first, matching how the basket is stored and how the checkout page showed it.
  const itemLines = (lineItems || []).filter(
    li => [LINE_ITEM_ITEM, LINE_ITEM_CART_ITEM].includes(li.code) && !li.reversal
  );
  const anchorFirst = [
    ...itemLines.filter(li => li.code === LINE_ITEM_ITEM),
    ...itemLines.filter(li => li.code === LINE_ITEM_CART_ITEM),
  ];
  const cartItemLines = anchorFirst;

  const snapshot = cart?.items || [];
  const extras = [
    ...snapshot.filter(item => item.listingId === cart?.anchorListingId),
    ...snapshot.filter(item => item.listingId !== cart?.anchorListingId),
  ];
  const titlesUsable = extras.length === cartItemLines.length;

  return (
    <>
      {cartItemLines.map((li, i) => {
        const title = titlesUsable ? extras[i]?.title : null;
        const quantity = lineItemQuantity(li.quantity);
        const label = title
          ? quantity > 1
            ? `${title} × ${quantity}`
            : title
          : intl.formatMessage({ id: 'OrderBreakdown.cartItemFallback' }, { quantity });

        return (
          <div key={`cart-item-${i}`} className={css.lineItem}>
            <span className={css.itemLabel}>{label}</span>
            <span className={css.itemValue}>{formatMoney(intl, li.lineTotal)}</span>
          </div>
        );
      })}
    </>
  );
};

export default LineItemCartItemsMaybe;
